import './QuestPage.css';
import { useEffect, useState, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AppStateContext } from '../../context/AppStateContext';

import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import DoneIcon from '@mui/icons-material/Done';

import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

// Próg podobieństwa (0–100 %) dla porównania przez API serwera
const SIMILARITY_THRESHOLD = 70;

function haversineDistanceMeters(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const QuestPage = ({socket, visible}) => {
    const [answer, setAnswer] = useState('');
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [wrongAnswerReveal, setWrongAnswerReveal] = useState(null);
    const [pointsPopup, setPointsPopup] = useState(null);
    const pointsPopupTimerRef = useRef(null);
    const {
        API_URL, 
        currentQuest, 
        teamToken,
        teamData,
        questNotification,
        playAnswerOkAudio,
        playWrongAnswerAudio,
    } = useContext(AppStateContext);
    const [showAR, setShowAR] = useState(false);
    const [showArTipsPopup, setShowArTipsPopup] = useState(false);
    const [showIosCameraHelpPopup, setShowIosCameraHelpPopup] = useState(false);

    // iPhone/iPad (Safari) - na tych urządzeniach iframe AR z aktywną kamerą
    // renderuje się zawsze nad resztą strony, więc automatyczne wykrywanie
    // braku uprawnień i modal błędu bywają niewidoczne. Zamiast tego pokazujemy
    // na sztywno przycisk pomocy z instrukcją nadania uprawnień ręcznie.
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

    const IOS_CAMERA_HELP_STEPS = [
        'Wejdź w Ustawienia na iPhonie.',
        'Wybierz "Prywatność i ochrona".',
        'Wybierz "Aparat".',
        'Wybierz przeglądarkę Chrome lub Safari lub innej której używasz.',
        'Włącz uprawnienia dla przeglądarki.',
        'Wróć do gry w przeglądarce i odśwież stronę.',
    ];

    const AR_TIPS = [
        'Skierujcie kamerę telefonu na płaskie, możliwie kontrastowe podłoże <span style="color:#ed7e7e">(najlepiej kostka brukowa, trawnik)</span>',
        'Przydzielcie aplikacji uprawnienia do korzystania z aparatu (jeśli po uruchomieniu AR widzicie czarne tło to prawdopodobnie aplikacja nie uzyskała uprawnień)',
        'Jeśli obiekt wyświetlił się w złym miejscu, lub w złej skali, użyjcie przycisku reset. Jeśli to nie pomoże to spróbujcie odświeżyć stronę.',
        'Trzymaj telefon w pionie, aby rozwiązać zagadkę.',
    ];

    // Stany dla lightbox
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [currentImageSrc, setCurrentImageSrc] = useState('');

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [model, setModel] = useState(null);
    const [result, setResult] = useState('');
    const [questResult, setQuestResult] = useState('');

    const [showVideo, setShowVideo] = useState(false);

    const [isProcessing, setIsProcessing] = useState(false);
    const [numberOfPassed, setNumberOfPassed] = useState(0);
    const [labelPassed, setLabelPassed] = useState('');
    const [imagesPassed, setImagesPassed] = useState([]);

    const [cameraError, setCameraError] = useState('');
    const [isCameraErrorModalOpen, setIsCameraErrorModalOpen] = useState(false);
    const [questResolvedData, setQuestResolvedData] = useState(null);
    const [vapiCallEndedInfo, setVapiCallEndedInfo] = useState(null);

    const refImgs = useRef([]);
    const playerImg = useRef(null);
    const [score, setScore] = useState(null);
    const [bestMatchIndex, setBestMatchIndex] = useState(null); // <- dodaj ten stan
    const [capturedCanvas, setCapturedCanvas] = useState(null);

    const [imagesCount, setImagesCount] = useState(0);

    // Geolokalizacyjna blokada treści zagadki (geo_unlock w quests.js)
    const [geoUnlocked, setGeoUnlocked] = useState(false);
    const [geoDistance, setGeoDistance] = useState(null);
    const [geoError, setGeoError] = useState('');
    const geoRequestSentRef = useRef(false);

    const contentRef = useRef(null);
    const [showScrollArrow, setShowScrollArrow] = useState(false);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;
        const checkScroll = () => {
            setShowScrollArrow(el.scrollHeight > el.clientHeight && el.scrollTop + el.clientHeight < el.scrollHeight - 10);
        };
        checkScroll();
        el.addEventListener('scroll', checkScroll);
        const ro = new ResizeObserver(checkScroll);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', checkScroll);
            ro.disconnect();
        };
    }, [currentQuest]);


    // Pomocnicza funkcja do ustawiania ref w mapie
    const setRefImg = (el, index) => {
        refImgs.current[index] = el;
    };


    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            const tracks = stream.getTracks();
            tracks.forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
        setShowVideo(false);
    };

    // Funkcje obsługi lightbox
    // const openImageModal = (imageSrc) => {
    //     setCurrentImageSrc(imageSrc);
    //     setIsImageModalOpen(true);
    // };

    const openImageModal = (item) => {
        setCurrentImageSrc(item);
        setIsImageModalOpen(true);
    };

    const closeImageModal = () => {
        setIsImageModalOpen(false);
        setCurrentImageSrc('');
    };

        // Komponent lightbox z react-zoom-pan-pinch
    const ImageLightbox = () => {
        if (!isImageModalOpen) return null;

        return createPortal(
            <div className="quest-page__image-modal">
                <div className="quest-page__image-modal-overlay" onClick={closeImageModal}></div>
                <div className="quest-page__image-modal-container">
                    <button 
                        className="quest-page__image-modal-close"
                        onClick={closeImageModal}
                        aria-label="Zamknij galerię"
                    >
                        ✖
                    </button>
                    
                    <TransformWrapper
                        initialScale={1}
                        minScale={0.5}
                        maxScale={5}
                        centerOnInit={true}
                        wheel={{
                            step: 0.1,
                            smoothStep: 0.005,
                        }}
                        pinch={{
                            step: 5,
                        }}
                        doubleClick={{
                            mode: "toggle",
                            animationTime: 300,
                        }}
                        pan={{
                            velocityEqualToMove: true,
                            animationTime: 200,
                        }}
                        options={{
                            limitToBounds: false,
                            centerContent: true,
                        }}
                    >
                        {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                            <>
                                {/* Kontrolki zoom */}
                                <div className="quest-page__zoom-controls">
                                    <button 
                                        className="quest-page__zoom-btn"
                                        onClick={() => zoomIn()}
                                        title="Powiększ"
                                    >
                                        +
                                    </button>
                                    <button 
                                        className="quest-page__zoom-btn"
                                        onClick={() => zoomOut()}
                                        title="Pomniejsz"
                                    >
                                        -
                                    </button>
                                    {/*
                                    <button 
                                        className="quest-page__zoom-btn"
                                        onClick={() => resetTransform()}
                                        title="Reset"
                                    >
                                        ⌂
                                    </button>
                                    */}
                                </div>

                                <TransformComponent
                                    wrapperClass="quest-page__transform-wrapper"
                                    contentClass="quest-page__transform-content"
                                >

                                <picture>
                                    {currentImageSrc.image_src_tablet_webp && <source media="(min-width: 768px)" srcSet={`${currentImageSrc.image_src_tablet_webp}`} type="image/webp" />}
                                    {currentImageSrc.image_src_tablet && <source media="(min-width: 768px)" srcSet={`${currentImageSrc.image_src_tablet}`} type="image/jpeg" />}
                                    {currentImageSrc.image_src_webp && <source srcSet={`${currentImageSrc.image_src_webp}`} type="image/webp" />}
                                    <source srcSet={`${currentImageSrc.image_src}`} type="image/jpeg" />
                                    <img 
                                        src={currentImageSrc.image_src} 
                                        alt="Quest photo" 
                                        className="quest-page__lightbox-image"
                                        draggable={false}
                                    />
                                </picture>

                                </TransformComponent>

                                {/* Instrukcje */}
                                {/*
                                <div className="quest-page__image-instructions">
                                 
                                    <p>👆 Podwójne kliknięcie: toggle zoom</p>
                                    <p>🖱️ Przeciągnij: przesuń obraz</p>
                                    <p>📱 Pinch: zoom na mobile</p>
                                </div>
                                */}
                            </>
                        )}
                    </TransformWrapper>
                </div>
            </div>,
            document.body
        );
    };

    function startCamera() {

        //if(checkDesktopMode()) return;// -> WŁĄCZYĆ

        const start = async () => {
            try {

                const stream = await navigator.mediaDevices.getUserMedia({
                    //video: true, // kamera na komputerze
                    video: {
                        facingMode: { exact: 'environment' }, // tylna kamera -> WŁĄCZYĆ
                    }
                });
                videoRef.current.srcObject = stream;
                await videoRef.current.play();

                setShowVideo(true);

            } catch (err) {
                console.warn("Nie udało się uruchomić tylnej kamery", err);

                setCameraError('Upewnij się, że przeglądarka ma dostęp do kamery. Sprawdź uprawnienia w ustawieniach przeglądarki. Po nadaniu uprawnień odśwież stronę i spróbuj ponownie.');
                setIsCameraErrorModalOpen(true);
    
            }
        };

        start();

    }

    // Zwraca Promise<boolean> - true gdy kamera jest dostępna. WAŻNE: na iOS
    // Safari iframe z aktywną kamerą renderuje się zawsze NAD resztą strony
    // (niezależnie od z-index/position:fixed), więc modal błędu byłby wizualnie
    // zasłonięty, gdyby iframe AR był już zamontowany. Dlatego wywołujący musi
    // poczekać na wynik tej funkcji i pokazać iframe dopiero po sukcesie.
    function checkCameraAvailability() {

        if(checkDesktopMode()) return Promise.resolve(false);

        const start = async () => {
            try {

                if (!navigator.mediaDevices?.getUserMedia) {
                    throw new Error('Ta przeglądarka nie obsługuje kamery.');
                }

                // Najpierw spróbuj sprawdzić stan uprawnienia BEZ wywoływania promptu.
                // Dzięki temu unikamy podwójnego pytania o zgodę: raz tutaj, drugi raz
                // wewnątrz iframe AR (silnik XR8 sam poprosi o dostęp do kamery).
                if (navigator.permissions?.query) {
                    try {
                        const status = await navigator.permissions.query({ name: 'camera' });

                        if (status.state === 'denied') {
                            throw new Error('camera-denied');
                        }

                        // 'granted' lub 'prompt' - o zgodę (jeśli trzeba) zapyta iframe AR
                        setCameraError('');
                        return true;
                    } catch (permErr) {
                        if (permErr?.message === 'camera-denied') {
                            throw permErr;
                        }
                        // Permissions API dla kamery nieobsługiwane (np. Safari/iOS w ogóle
                        // go nie wspiera dla kamery) - użyj fallbacku poniżej
                    }
                }

                // Fallback: tylko test dostępu do kamery
                const stream = await navigator.mediaDevices.getUserMedia({ 
                    video: true,
                    audio: false
                });

                // zamknij od razu testowy stream
                stream.getTracks().forEach((track) => track.stop());
                setCameraError('');
                return true;

            } catch (err) {
                console.warn("Nie udało się uruchomić tylnej kamery", err);

                setCameraError('Upewnij się, że przeglądarka ma dostęp do kamery. Sprawdź uprawnienia w ustawieniach przeglądarki. Po nadaniu uprawnień odśwież stronę i spróbuj ponownie.');
                setIsCameraErrorModalOpen(true);
                return false;
            }
        };

        return start();
    }

    function checkDesktopMode() {
        const isDesktop = !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

        console.log('isDesktop', isDesktop);

        if (isDesktop) {
            setCameraError('Sprawdź w ustawieniach przeglądarki czy opcja \'Wersja na komputer\' jest wyłączona. Po zmianie tej opcji odśwież stronę i spróbuj ponownie.');
            setIsCameraErrorModalOpen(true);

            return true;
        }

        return false;
    }
  

    async function saveDetectedImagesToDatabase(label) {
      
        // Zapisz wykryte etykiety do bazy
        try {
            await fetch(`${API_URL}/api/team/detected-images`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    teamToken: teamToken,
                    questId: currentQuest.id,
                    detectedLabel: label
                })
            })
            
        } catch (err) {
            console.error('❌ Błąd zapisu wykrytych obrazów:', err);
        }
            
    }

    useEffect(() => {
        if (!currentQuest || !currentQuest.image_detection_message) return;

        const detectionCount = currentQuest.content
            ?.flatMap(item => item.image_detection || []).length || 0;

        if (detectionCount > 0 && imagesPassed.length >= detectionCount) {
            playAnswerOkAudio();

            const nextMessage = {
                id: 'answer-ok',
                next_message_id: currentQuest.image_detection_message.next_message_id,
                quest_id: currentQuest.id,
                team_token: teamToken,
                points: currentQuest.points
            };

            socket.emit("message", nextMessage);
        }
    }, [imagesPassed]);


    // Pobierz wykryte obrazy dla bieżącej zagadki
    useEffect(() => {

        if(!currentQuest || !currentQuest.image_detection_message) return;


        const fetchDetectedImages = async () => {
            if (!currentQuest || !teamToken) return;

            try {
                const response = await fetch(`${API_URL}/api/team/detected-images?teamToken=${teamToken}&questId=${currentQuest.id}`);
                const data = await response.json();

                console.log('Fetched detected images data:', data);
                
                if (data.success && data.detectedLabels) {
                    setImagesPassed(data.detectedLabels);
                    console.log('✅ Pobrano wykryte obrazy dla zagadki:', data.detectedLabels);
                } else {
                    // Brak zapisanych obrazów, zresetuj
                    setImagesPassed([]);
                }
            } catch (err) {
                console.error('❌ Błąd pobierania wykrytych obrazów:', err);
                setImagesPassed([]);
            }
        };

        fetchDetectedImages();


    }, [currentQuest]);

    useEffect(() => {

        if(!currentQuest) return;

        const getImageCount = () => {
            let count = 0;

            console.log('currentQuest', currentQuest);

            currentQuest.content.forEach(item => {
                if(item.image_detection) {
                    count += item.image_detection.length;
                }
                if(item.type === 'image' && item.image_src) {
                    count += 1;
                }
            });

            return count;
        }

        const imageCount = getImageCount();
        setImagesCount(imageCount);

    }, [currentQuest]);

    useEffect(() => {
        if (!questNotification) {
            setAnswer('');
            setSuccess(false);
            setError(false);
            setWrongAnswerReveal(null);
        }
    }, [questNotification]);

    useEffect(() => {
        setAnswer('');
        setSuccess(false);
        setError(false);
        setWrongAnswerReveal(null);

        setShowAR(false);
    }, [currentQuest]);

    // reset stanu geolokalizacji przy zmianie zagadki
    useEffect(() => {
        setGeoUnlocked(false);
        setGeoDistance(null);
        setGeoError('');
        geoRequestSentRef.current = false;
    }, [currentQuest?.id]);

    // strefa mogła zostać odblokowana wcześniej (np. po odświeżeniu strony)
    useEffect(() => {
        if (currentQuest && teamData?.geoUnlockedQuests?.includes(currentQuest.id)) {
            setGeoUnlocked(true);
        }
    }, [currentQuest, teamData]);

    // ktoś z drużyny wszedł w strefę - odblokuj treść u wszystkich
    useEffect(() => {
        const handleGeoUnlocked = (data) => {
            if (data.questId === currentQuest?.id) {
                setGeoUnlocked(true);
                playAnswerOkAudio();
            }
        };

        socket.on("geoUnlocked", handleGeoUnlocked);

        return () => {
            socket.off("geoUnlocked", handleGeoUnlocked);
        };
    }, [currentQuest]);

    // śledzenie pozycji GPS dopóki strefa nie zostanie odblokowana
    useEffect(() => {
        if (!currentQuest?.geo_unlock || geoUnlocked || !teamToken) return;

        if (!navigator.geolocation) {
            setGeoError('Ta przeglądarka nie obsługuje geolokalizacji.');
            return;
        }

        const [targetLng, targetLat] = currentQuest.geo_unlock.coordinates;
        const radius = currentQuest.geo_unlock.radius || 30;

        const watchId = navigator.geolocation.watchPosition(
            (pos) => {
                const distance = haversineDistanceMeters(
                    pos.coords.latitude,
                    pos.coords.longitude,
                    targetLat,
                    targetLng
                );
                setGeoDistance(Math.round(distance));
                setGeoError('');

                if (distance <= radius && !geoRequestSentRef.current) {
                    geoRequestSentRef.current = true;

                    fetch(`${API_URL}/api/team/geo-unlock`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            teamToken: teamToken,
                            questId: currentQuest.id,
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude
                        })
                    })
                    .then(res => res.json())
                    .then(data => {
                        if (!data.success) {
                            // serwer odrzucił (np. inna odległość) - pozwól spróbować ponownie
                            geoRequestSentRef.current = false;
                        }
                    })
                    .catch(err => {
                        console.error('Błąd odblokowania strefy:', err);
                        geoRequestSentRef.current = false;
                    });
                }
            },
            (err) => {
                console.warn('Błąd geolokalizacji:', err);
                setGeoError('Nie można ustalić Waszej pozycji. Upewnijcie się, że aplikacja ma dostęp do lokalizacji i macie włączony GPS.');
            },
            { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
        );

        return () => navigator.geolocation.clearWatch(watchId);
    }, [currentQuest, geoUnlocked, teamToken]);

    useEffect(() => {

        if (!teamToken) return;

        const handleQuestResolved = async (data) => {
            console.log('ktoś rozwiązał zagadkę', data);
            const isSolver = data.solverSocketId === socket.id;
            setQuestResolvedData({ ...data, isSolver });
            showPointsPopup(data.questId);
        }

        socket.on("questResolved", handleQuestResolved);

        return () => {
            socket.off("questResolved", handleQuestResolved);
        };

    }, [currentQuest]);

    useEffect(() => {
        if (!teamToken) return;

        const handleVapiCallEnded = (data) => {
            setVapiCallEndedInfo(data);
        };

        socket.on("vapiCallEnded", handleVapiCallEnded);

        return () => {
            socket.off("vapiCallEnded", handleVapiCallEnded);
        };
    }, [teamToken]);

    useEffect(() => {
        if (teamData?.vapiCallEnded) {
            setVapiCallEndedInfo(teamData.vapiCallEnded);
        }
    }, [teamData]);

    const handleInputChange = (e) => {
        setAnswer(e.target.value);
        setError(false);
    };

    const handleAnswerInputFocus = () => {
        const container = document.querySelector('.quest-page__content');
        if (!container) return;

        const scrollInputIntoView = () => {
            const inputEl = container.querySelector('.quest-page__input input:focus, .Mui-focused input');
            if (!inputEl) return;

            const inputRect = inputEl.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const offset = inputRect.bottom - containerRect.bottom + 40;

            if (offset > 0) {
                container.scrollBy({ top: offset, behavior: 'smooth' });
            }
        };

        requestAnimationFrame(scrollInputIntoView);

        if (!window.visualViewport) {
            setTimeout(scrollInputIntoView, 250);
            return;
        }

        const onViewportResize = () => requestAnimationFrame(scrollInputIntoView);
        window.visualViewport.addEventListener('resize', onViewportResize);

        setTimeout(() => {
            window.visualViewport?.removeEventListener('resize', onViewportResize);
        }, 1200);
    };

    // normalizacja odpowiedzi: wielkość liter, nadmiarowe spacje i polskie znaki
    // (ą->a, ł->l itd.) nie mogą decydować o uznaniu odpowiedzi
    const normalizeAnswer = (value) => String(value)
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ' ')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/ł/g, 'l');

    // powiadomienie o punktach: pełna pula bez podpowiedzi, połowa gdy użyto
    // podpowiedzi w tej zagadce; wywoływane ze zdarzenia questResolved, więc
    // obejmuje też zagadki AR/detekcję obrazu i nie odpala się przy ponownej
    // odpowiedzi na już rozwiązaną zagadkę ani przy pominięciu zagadki
    const showPointsPopup = (questId) => {
        const hintUsed = teamData?.hintsUsed?.some(h => h.quest_id === questId);
        const basePoints = (currentQuest?.id === questId && currentQuest.points) || 200;
        //const points = hintUsed ? 100 : basePoints;
        const points = basePoints;
        clearTimeout(pointsPopupTimerRef.current);
        setPointsPopup(points);
        // 2s pełnej widoczności + 0.6s płynnego zanikania (zgodnie z animacją CSS)
        pointsPopupTimerRef.current = setTimeout(() => setPointsPopup(null), 2600);
    };

    useEffect(() => {
        return () => clearTimeout(pointsPopupTimerRef.current);
    }, []);

    const handleCheckAnswer = (index) => {
        const inputData = currentQuest.content[index].input;

        if(!inputData || !teamToken) {
            return;
        }

        const answerOk = normalizeAnswer(currentQuest.content[index].input.answer_ok);
        const userAnswer = normalizeAnswer(answer);

        if (userAnswer === answerOk) {
            setError(false);
            setSuccess(true);

            playAnswerOkAudio();

            const nextMessage = {
                id: 'answer-ok',
                next_message_id: inputData.next_message_id_ok,
                quest_id: currentQuest.id,
                team_token: teamToken,
                points: currentQuest.points,
                input_answer: inputData.answer_ok
            }

            socket.emit("message", nextMessage);
            
        } else if (inputData.single_attempt) {
            setError(false);
            playWrongAnswerAudio();

            setWrongAnswerReveal({ correctAnswer: inputData.answer_ok, revealText: inputData.answer_no_reveal_text });

            const nextMessage = {
                id: 'answer-no',
                next_message_id: inputData.next_message_id_no,
                quest_id: currentQuest.id,
                team_token: teamToken
            }

            socket.emit("message", nextMessage);
        } else {
            const answersArray = (inputData.answers || []).map(normalizeAnswer);

            if(answersArray.indexOf(userAnswer) === -1) {
                setError(true);
                playWrongAnswerAudio();
            } else {

                setError(false);
                setSuccess(true);

                playAnswerOkAudio();

                const nextMessage = {
                    id: 'answer-ok',
                    next_message_id: inputData.next_message_id_ok,
                    quest_id: currentQuest.id,
                    team_token: teamToken,
                    points: currentQuest.points,
                    input_answer: inputData.answer_ok
                }
                socket.emit("message", nextMessage);
            }

        }
    };

    const handleShowARIframe = () => {
        //startCamera();
        checkCameraAvailability();
        setShowAR(true);
    };

    // const btnCss = () => ({
    //     color: '#fff',
    //     background: 'linear-gradient(45deg, #2c3955, #2b65d9)',
    //     border: '1px solid rgb(255 255 255 / 35%)',
    //     boxShadow: 'inset 0 4px 10px rgba(0, 0, 0, 0.3)',
    //     textTransform: 'none',
    //     fontWeight: 300,
    //     height: '56px',
    //     marginTop: '8px'
    // });

    const btnCss = () => ({
        fontSize: '18px',
        marginTop: '8px',
        color: '#31bdff',
        borderColor: 'rgba(128, 220, 255, .5)',
        padding: '10px 20px',
        '&:hover': {
            borderColor: '#80dcff'
        },
        '&:disabled': {
            color: 'rgba(49, 189, 255, 0.5)',
            borderColor: 'rgba(128, 220, 255, 0.3)',
            opacity: .2
        }
    });

    const textFieldCss = {
        marginTop: '22px',
        '& .MuiTextField-root': { width: '100%' },
        '& .MuiInputBase-input': {
            background: 'rgb(255 255 255 / 12%)',
            fontSize: '1.6rem', 
            color: '#31bdff'
        },
        '& .MuiInputLabel-root': { 
            fontSize: '1.6rem', 
            color: '#31bdff',
            
            '&.Mui-focused': {
                color: '#31bdff',
            }
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: 'rgba(128, 220, 255, .5)',
            },
            '&:hover fieldset': {
                borderColor: '#80dcff',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#31bdff',
            },
        },
        '.MuiFormHelperText-root.Mui-error': {
            fontSize: '1.2rem',
            margin: '8px 0'
        },
        '.MuiInputBase-root::after': {
            //border: 'none',
            width: 'calc(100% - 39px)'
        },
        '.MuiInputBase-root.MuiFilledInput-root::before': {
            borderBottom: 'none',
        }
    };

    const TOTAL_QUESTS = 20;
    const currentQuestNumber = parseInt(currentQuest?.id.split('-')[1]) || 0;
    const progressPercent = Math.min((currentQuestNumber / TOTAL_QUESTS) * 100, 100);

    const geoLocked = !!(currentQuest?.geo_unlock && !geoUnlocked);

    // ===== STARE ROZWIĄZANIE (ORB / OpenCV) - zakomentowane =====
    /*
    // Helper - czyta obraz w pełnym rozmiarze przez canvas
    const readFullSizeImage = (cv, imgElement) => {
        const canvas = document.createElement('canvas');
        canvas.width = imgElement.naturalWidth;
        canvas.height = imgElement.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(imgElement, 0, 0, imgElement.naturalWidth, imgElement.naturalHeight);


        return cv.imread(canvas);
    };
    */

    // Funkcja robienia zdjęcia
    const takePhoto2 = () => {
        setShowVideo(false);

        const video = videoRef.current;
        // const canvas = canvasRef.current;
        // canvas.width = video.videoWidth;
        // canvas.height = video.videoHeight;
        // canvas.getContext('2d').drawImage(video, 0, 0);
        // Stwórz NOWY canvas zamiast używać canvasRef
        setIsProcessing(true);
        setResult('');
        
        const newCanvas = document.createElement('canvas');
        newCanvas.width = video.videoWidth;
        newCanvas.height = video.videoHeight;
        newCanvas.getContext('2d').drawImage(video, 0, 0);

        if (newCanvas.width !== 0 && newCanvas.height !== 0) {
            setCapturedCanvas(newCanvas);
        } else {
            setIsProcessing(false);
            setResult('❌ Nie udało się zrobić zdjęcia. Spróbuj ponownie. (takePhoto2)');
        }

        //setShowVideo(false); // schowaj kamerę po zdjęciu
        stopCamera();
    };

    // ===== STARE ROZWIĄZANIE (ORB / OpenCV) - zakomentowane =====
    /*
    const compareWithCamera = async () => {
        console.log('compareWithCamera called');
        const cv = window.cv;
        if (!cv || !capturedCanvas) {
            //setIsProcessing(false);
            setIsProcessing(false);
            setResult('❌ Nie udało się przetworzyć zdjęcia. Spróbuj ponownie. (!cv || !capturedCanvas)');
            return;
        }

        // Sprawdź czy są jakieś obrazy referencyjne
        console.log('capturedCanvas', capturedCanvas);

        // Sprawdź czy canvas ma poprawny rozmiar
        if (capturedCanvas.width === 0 || capturedCanvas.height === 0) {
            console.warn('Canvas ma zerowy rozmiar:', capturedCanvas.width, capturedCanvas.height);
            setIsProcessing(false);
            setResult('❌ Nie udało się przetworzyć zdjęcia. Spróbuj ponownie.');
            return;
        }

        const img2 = cv.imread(capturedCanvas);
        let bestScore = 0;
        let bestIndex = -1; // <- dodaj



        // Porównaj z każdym obrazem wzorcowym
        for (let i = 0; i < refImgs.current.length; i++) {
            const refElement = refImgs.current[i];
            if (!refElement) continue;

            const img1 = readFullSizeImage(cv, refElement);

            console.log('img1', img1.cols, img1.rows, 'img2', img2.cols, img2.rows);

            const gray1 = new cv.Mat();
            const gray2 = new cv.Mat();
            cv.cvtColor(img1, gray1, cv.COLOR_RGBA2GRAY);
            
            cv.cvtColor(img2, gray2, cv.COLOR_RGBA2GRAY);

            const orb = new cv.ORB(500);
            const kp1 = new cv.KeyPointVector();
            const kp2 = new cv.KeyPointVector();
            const desc1 = new cv.Mat();
            const desc2 = new cv.Mat();

            orb.detectAndCompute(gray1, new cv.Mat(), kp1, desc1);
            orb.detectAndCompute(gray2, new cv.Mat(), kp2, desc2);

            console.log("kp1:", kp1.size(), "kp2:", kp2.size());

            if (!desc1.empty() && !desc2.empty()) {
                const bf = new cv.BFMatcher(cv.NORM_HAMMING, false);
                const matchesVec = new cv.DMatchVectorVector();
                bf.knnMatch(desc1, desc2, matchesVec, 2);

                let good = 0;
                for (let j = 0; j < matchesVec.size(); j++) {
                    const m = matchesVec.get(j).get(0);
                    const n = matchesVec.get(j).get(1);
                    if (m.distance < 0.85 * n.distance) good++;
                }

                console.log(`Obraz ${i}: ${good} dopasowań`);
                if (good > bestScore) {
                    bestScore = good;
                    bestIndex = i; // <- zapisuj indeks
                }

                matchesVec.delete();
                bf.delete();
            }

            img1.delete();
            gray1.delete(); gray2.delete();
            desc1.delete(); desc2.delete();
            kp1.delete(); kp2.delete();
        }

        img2.delete();
        setScore(bestScore);
        setBestMatchIndex(bestIndex); // <- ustaw indeks (dla testów)

        setIsProcessing(false);


        // wynik ustawiany zawsze, niezależnie od tego czy indeks się zmienił
        if (bestIndex !== -1 && bestScore > 100) {
            setImagesPassed(prev => {
                if (!prev.includes(bestIndex)) {
                    saveDetectedImagesToDatabase(bestIndex);
                    setResult('✅ Brawo! zaliczono 1 zdjęcie');
                    return [...prev, bestIndex];
                }
                setResult('❌ Zdjęcie już wcześniej zaliczone');
                return prev;
            });
        } else {
            setResult('❌ Nie rozpoznano zdjęcia. Spróbuj ponownie.');
        }


        console.log('Dopasowane punkty:', bestScore, 'obrazek:', bestIndex + 1);
    };
    */

    // Porównanie zdjęcia z kamery z obrazami wzorcowymi przez API serwera
    const compareWithCamera = async () => {
        if (!capturedCanvas || capturedCanvas.width === 0 || capturedCanvas.height === 0) {
            setIsProcessing(false);
            setResult('❌ Nie udało się przetworzyć zdjęcia. Spróbuj ponownie.');
            return;
        }

        // Zbierz URL-e obrazów wzorcowych z bieżącej zagadki
        const refUrls = currentQuest?.content
            ?.flatMap(item => item.image_detection || [])
            ?.map(img => ({ main: img.image_src, variants: img.variants || [] })) || [];

        if (refUrls.length === 0) {
            setIsProcessing(false);
            setResult('❌ Brak obrazów referencyjnych.');
            return;
        }

        try {
            const blob = await new Promise((resolve, reject) => {
                capturedCanvas.toBlob(
                    (b) => (b ? resolve(b) : reject(new Error('toBlob zwrócił null'))),
                    'image/jpeg',
                    0.92
                );
            });

            const formData = new FormData();
            formData.append('photo', blob, 'photo.jpg');
            formData.append('reference_urls', JSON.stringify(refUrls));

            const res = await fetch(`${API_URL}/api/compare-photo-url`, {
                method: 'POST',
                body: formData,
            });

            const data = await res.json();

            if (!res.ok) {
                setIsProcessing(false);
                setResult(`❌ ${data.error || 'Błąd serwera'}`);
                return;
            }

            let bestSimilarity = -1;
            let bestIndex = -1;

            (data.results || []).forEach(r => {
                if (r.similarity > bestSimilarity) {
                    bestSimilarity = r.similarity;
                    bestIndex = r.index;
                }
            });

            console.log('Najlepsze podobieństwo:', bestSimilarity, '% — obrazek:', bestIndex + 1);

            setScore(bestSimilarity);
            setBestMatchIndex(bestIndex);
            setIsProcessing(false);

            if (bestIndex !== -1 && bestSimilarity >= SIMILARITY_THRESHOLD) {
                setImagesPassed(prev => {
                    if (!prev.includes(bestIndex)) {
                        saveDetectedImagesToDatabase(bestIndex);
                        setResult('✅ Brawo! zaliczono 1 zdjęcie');
                        return [...prev, bestIndex];
                    }
                    setResult('✅ Zdjęcie już wcześniej zaliczone');
                    return prev;
                });
            } else {
                setResult('❌ Nie rozpoznano zdjęcia. Spróbuj ponownie.');
            }

        } catch (err) {
            console.error('Błąd porównania:', err);
            setIsProcessing(false);
            setResult('❌ Błąd połączenia z serwerem.');
        }
    };

    useEffect(() => {
        if (capturedCanvas) {
            compareWithCamera();
        }
    }, [capturedCanvas]);


    // useEffect(() => {
    //     if (bestMatchIndex !== null && score > 100) {
    //         setImagesPassed(prev => {
    //             if (!prev.includes(bestMatchIndex)) {
    //                 saveDetectedImagesToDatabase(bestMatchIndex);
    //                 return [...prev, bestMatchIndex];
    //             } else {
    //                 setResult(`❌ Zdjęcie już wcześniej zaliczone`);
    //             }
    //             return prev;
    //         });

    //         setResult(`✅ Brawo! zaliczono 1 zdjęcie`);

    //     }

    //     if(bestMatchIndex !== null && score <= 100) {
    //         setResult(`❌ Nie rozpoznano zdjęcia. Spróbuj ponownie.`);
    //     }


    // }, [bestMatchIndex]);

    return (
        <div className={`quest-page ${visible ? '-visible' : '-hide'}`}>
            {pointsPopup && createPortal(
                <div className='points-popup'>+{pointsPopup} pkt</div>,
                document.body
            )}

            <div className={`quest-page__content`} ref={contentRef}>

                {<div className={`quest-page__camera-container ${showVideo ? '-show' : '-hide'}`}>
                    <button
                        className='quest-page__camera-close-btn'
                        onClick={stopCamera}
                        aria-label="Zamknij kamerę"
                    >
                        ✖
                    </button>
                    <video className='quest-page__camera-video' ref={videoRef} />
                    {/*<canvas ref={canvasRef} style={{ display: 'none' }} />*/}
                    {!isProcessing && <div style={{ marginTop: 10 }}>
                        <Button 
                            variant="outlined" 
                            onClick={() => { takePhoto2(); setShowVideo(false); }} 
                            sx={btnCss()}
                        >
                            Zrób zdjęcie
                        </Button>
                    </div>}
                    
                    {/*
                    {result && <h3 className='quest-page__camera-result'>{result}</h3>}
                    */}
                </div>}

            {currentQuest && geoLocked && (
                <div className="quest-page__geo-lock">
                    <div className="quest-page__geo-lock-icon">📍</div>
                    <h3 className="quest-page__geo-lock-title">Strefa zadania</h3>
                    <p className="quest-page__geo-lock-text">
                        {currentQuest.geo_unlock.locked_text || 'Udajcie się w miejsce zaznaczone na mapie, aby odblokować dalszą część zagadki.'}
                    </p>
                    {currentQuest.geo_unlock.image_src && (
                        <div className="quest-page__geo-lock-img-wrapper">
                            <picture>
                                {currentQuest.geo_unlock.image_src_tablet_webp && <source media="(min-width: 768px)" srcSet={`${currentQuest.geo_unlock.image_src_tablet_webp}`} type="image/webp" />}
                                {currentQuest.geo_unlock.image_src_tablet && <source media="(min-width: 768px)" srcSet={`${currentQuest.geo_unlock.image_src_tablet}`} type="image/jpeg" />}
                                {currentQuest.geo_unlock.image_src_webp && <source srcSet={`${currentQuest.geo_unlock.image_src_webp}`} type="image/webp" />}
                                <source srcSet={`${currentQuest.geo_unlock.image_src}`} type="image/jpeg" />
                                <img
                                    src={currentQuest.geo_unlock.image_src}
                                    alt="Miejsce zadania"
                                    className="quest-page__geo-lock-img"
                                />
                            </picture>
                        </div>
                    )}
                    {!geoError && geoDistance === null && (
                        <div className="quest-page__geo-lock-status">
                            <CircularProgress size={20} sx={{ color: '#31bdff' }} />
                            <span>Ustalanie Waszej pozycji…</span>
                        </div>
                    )}
                    {!geoError && geoDistance !== null && (
                        <div className="quest-page__geo-lock-status">
                            <span>
                                Odległość od strefy:{' '}
                                <strong>
                                    {Math.max(0, geoDistance - (currentQuest.geo_unlock.radius || 30))} m
                                </strong>
                            </span>
                        </div>
                    )}
                    {geoError && <p className="quest-page__geo-lock-error">{geoError}</p>}
                </div>
            )}

            {currentQuest && !geoLocked && currentQuest.content.map((item, index) => {
                if (item.input) {
                    return (
                        <div key={index} className="quest-page__item">
                            <Box
                                component="form"
                                sx={{ '& .MuiTextField-root': { m: 1, width: '100%' } }}
                                noValidate
                                autoComplete="off"
                            >
                                <div className='quest-page__input-container'>
                                    <TextField
                                        error={error}
                                        id="outlined-error"
                                        label="Wpisz odpowiedź"
                                        variant="filled"
                                        size="normal"
                                        disabled={!!wrongAnswerReveal}
                                        helperText={error ? "Niestety, to nie jest poprawna odpowiedź. Spróbuj ponownie." : ""}
                                        className='quest-page__input'
                                        onChange={handleInputChange}

                                        value={answer}
                                        sx={textFieldCss}

                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => handleCheckAnswer(index)}
                                                            edge="end"
                                                            disabled={!!wrongAnswerReveal}
                                                            sx={{
                                                                color: '#fff',
                                                                background: 'rgb(18 136 208 / 34%)'
                                                            }}
                                                        >
                                                            <SendIcon />

                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }
                                        }}

                                        onFocus={handleAnswerInputFocus}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                console.log('Enter');
                                                e.preventDefault();
                                                handleCheckAnswer(index);
                                            }
                                        }}
                                    />
                                    {success && <div className='quest-page__item-success-info'>
                                        Brawo! Twoja odpowiedź jest poprawna.
                                    </div>}
                                    {/*
                                    <Button 
                                        variant="outlined" 
                                        endIcon={<SendIcon className='quest-page__input-icon'/>} 
                                        sx={btnCss()} 
                                        onClick={() => handleCheckAnswer(index)} 
                                        disabled={success}
                                    >
                                        Wyślij odpowiedź
                                    </Button>
                                    */}
                                </div>
                            </Box>

                        </div>
                    )
                }
                return (
                    <div key={index} className={`quest-page__item ${imagesCount > 1 &&  item.type === 'image' ? '-more-images' : ''}`}>
                        {item.type === 'image' && item.image_src && 
                            <div className="quest-page__item-img-wrapper" onClick={() => openImageModal(item)}>
                                <picture>
                                    {item.image_src_tablet_webp && <source media="(min-width: 768px)" srcSet={`${item.image_src_tablet_webp}`} type="image/webp" />}
                                    {item.image_src_tablet && <source media="(min-width: 768px)" srcSet={`${item.image_src_tablet}`} type="image/jpeg" />}
                                    {item.image_src_webp && <source srcSet={`${item.image_src_webp}`} type="image/webp" />}
                                    <source srcSet={`${item.image_src}`} type="image/jpeg" />
                                    <img 
                                        src={item.image_src} 
                                        alt="Quest photo" 
                                        className="quest-page__item-img"
                                        style={{ cursor: 'zoom-in' }}
                                    />
                                </picture>
                                <ZoomInIcon 
                                    className='quest-page__item-zoom-icon' 
                                    sx={{
                                        marginLeft: '-33px',
                                        color: '#fff',
                                        width: '30px',
                                        height: '30px',
                                        background: '#0000007a',
                                        borderRadius: '50%',
                                        marginBottom: '3px',
                                        padding: '2px'
                                    }}
                                />
                            </div>
                        }
                        {item.type === 'camera' && item.image_detection &&
                        <div className='quest-page__item-image-detection'>
                            {/*}
                            <div className='quest-page__item-image-detection-list'>
                                {item.image_detection.map((image) => {
                                    return (
                                        <div className="quest-page__item-img-wrapper" onClick={() => openImageModal(image.image_src)}>
                                            <picture>
                                                {image.image_src_tablet_webp && <source media="(min-width: 768px)" srcSet={`${item.image_src_tablet_webp}`} type="image/webp" />}
                                                {image.image_src_tablet && <source media="(min-width: 768px)" srcSet={`${item.image_src_tablet}`} type="image/jpeg" />}
                                                {image.image_src_webp && <source srcSet={`${image.image_src_webp}`} type="image/webp" />}
                                                <source srcSet={`${image.image_src}`} type="image/jpeg" />
                                                <img 
                                                    src={image.image_src} 
                                                    alt="Quest photo" 
                                                    className="quest-page__item-img-detection"
                                                    style={{ cursor: 'zoom-in', width: '70px' }}
                                                />
                                            </picture>
                                            <ZoomInIcon 
                                                className='quest-page__item-zoom-icon' 
                                                sx={{
                                                    marginLeft: '-33px',
                                                    color: '#fff',
                                                    width: '30px',
                                                    height: '30px',
                                                    background: '#0000007a',
                                                    borderRadius: '8px',
                                                    marginBottom: '3px'
                                                }}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                            <div className='quest-page__detection-result'>{questResult}</div>
                            <Button 
                                variant="outlined" 
                                sx={btnCss()} 
                                onClick={() => {
                                    setShowVideo(true);
                                    startCamera();
                                }}
                            >
                                📷 Włącz kamerę
                            </Button>
                            */}

                                {item.image_detection.map((image, i) => (
                                    
                                   
                                        <img
                                            key={i}
                                            ref={(el) => setRefImg(el, i)}
                                            crossOrigin="anonymous"
                                            src={image.image_src}
                                            alt={`ref-${i}`}
                                            style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}
                                            onLoad={(e)=>{
                                            console.log(
                                                "loaded",
                                                e.target.naturalWidth,
                                                e.target.naturalHeight
                                            );
                                        }}
                                        />
                                   
                                ))}

                                {/* Obraz wzorcowy - ukryty, bez skalowania CSS */}
                                {/*
                                <img 
                                    ref={refImg} 
                                    crossOrigin="anonymous" 
                                    src="/assets/images_test/IMG_0018.jpg" 
                                    alt="ref" 
                                    style={{ position: 'absolute', visibility: 'hidden', width: 'auto', height: 'auto', pointerEvents: 'none' }}
                                    onLoad={() => console.log("✅ refImg załadowany:", refImg.current?.naturalWidth, refImg.current?.naturalHeight)}
                                />*/}

                                {/* Podgląd dla użytkownika - w pętli */}
                                <div className="quest-page__item-image-detection-list">
                                    {item.image_detection.map((image, i) => (
                                        <div 
                                            key={i}
                                            className={
                                                `quest-page__item-img-wrapper  
                                                ${imagesPassed.includes(i) ? '-done' : ''}`
                                            }
                                            onClick={() => openImageModal(image)}
                                        >
                                            <img 
                                                src={image.image_miniature_src} 
                                                alt={`Wzorzec ${i + 1}`} 
                                                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                            />
                                            <ZoomInIcon 
                                                className='quest-page__item-zoom-icon' 
                                                sx={{
                                                    marginLeft: '-33px',
                                                    color: '#fff',
                                                    width: '30px',
                                                    height: '30px',
                                                    background: '#0000007a',
                                                    borderRadius: '50%',
                                                    marginBottom: '3px',
                                                    padding: '2px'
                                                }}
                                            />
                                            <DoneIcon 
                                                className='quest-page__item-done-icon'
                                                sx={{
                                                    fontSize: '40px',
                                                    display: 'none'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {/* Podgląd dla użytkownika - osobny element */}
                                {/*
                                <div className="quest-page__item-img-wrapper" onClick={() => openImageModal('/assets/images_test/IMG_0018.jpg')}>
                                    <img 
                                        src="/assets/images_test/IMG_0018.jpg" 
                                        alt="Wzorzec" 
                                        style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                                    />
                                    <ZoomInIcon 
                                        className='quest-page__item-zoom-icon' 
                                        sx={{
                                            marginLeft: '-33px',
                                            color: '#fff',
                                            width: '30px',
                                            height: '30px',
                                            background: '#0000007a',
                                            borderRadius: '50%',
                                            marginBottom: '3px',
                                            padding: '2px'
                                        }}
                                    />

                               </div>
                               */}

                               {/* Podgląd zdjęcia z kamery */}
                                {capturedCanvas && (
                                    <div className='quest-page__camera-preview' style={{ position: 'relative', display: 'flex' }}>
                                        <div style={{ marginTop: '10px', color: '#fff' }}>Zdjęcie z kamery:</div>
                                        <img 
                                            src={capturedCanvas.toDataURL()} 
                                            alt="Twoje zdjęcie" 
                                            style={{ width: '150px', height: '150px', display: 'block', marginBottom: '10px' }}
                                        />
                                        {/*
                                        <div style={{color: '#fff', fontSize: '12px'}}>
                                            Rozmiar: {capturedCanvas.width}x{capturedCanvas.height}
                                        </div>
                                        */}
                                        { isProcessing && (
                                            <div style={{
                                                position: 'absolute',
                                                top: '30px',
                                                left: 0,
                                                width: '100%',
                                                height: '160px',
                                                background: 'rgba(0,0,0,0.5)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '10px',
                                                color: '#83caff',
                                                fontSize: '14px',
                                                display: 'flex',
                                                fontSize: '15px',
                                            }}>
                                                <div style={{
                                                    border: '6px solid rgba(255, 255, 255, 0.3)',
                                                    borderTop: '6px solid #83caff',
                                                    borderRadius: '50%',
                                                    width: '30px',
                                                    height: '30px',
                                                    animation: 'spin 1s linear infinite'
                                                }}></div>
                                                Analizuję zdjęcie...
                                            </div>
                                        )}
                                    </div>
                                )}

                                {result && <h3 className='quest-page__camera-result'>{result}</h3>}

                                <br />

                                <Button variant="outlined" sx={btnCss()} onClick={() => {
                                    //setShowVideo(true);
                                    startCamera();
                                }}>
                                    Zrób zdjęcie
                                </Button>

                                <br />

                                {/*<Button variant="outlined" sx={btnCss()} onClick={compareWithCamera}>Porównaj</Button> */}

                                {/*
                                {<div style={{color: '#fff'}}>Dopasowane punkty:{score}</div>}
                                */}

                                {/*
                                {score !== null && (
                                    <div style={{color: '#fff'}}>
                                        Dopasowane punkty: {score}
                                        {bestMatchIndex !== null && bestMatchIndex >= 0 && (
                                            <span style={{marginLeft: '10px', color: '#31bdff'}}>
                                                (obrazek nr {bestMatchIndex + 1})
                                            </span>
                                        )}
                                    </div>
                                )}
                                */}

                        </div>}
                        {item.text && <p className="quest-page__item-text" dangerouslySetInnerHTML={{ __html: item.text }} />}

                        

                        {item.text && (vapiCallEndedInfo || teamData?.vapiCallEnded) && currentQuest.vapi_call && (
                            <div className="quest-page__item-text">
                                <h3 style={{color: '#77e5a9', marginTop: '20px'}}>✔ Telefon wykonany</h3>
                                <p dangerouslySetInnerHTML={{ __html: (vapiCallEndedInfo || teamData?.vapiCallEnded).text }} />
                                {/*<p style={{marginTop: '10px 0'}}>Brawo! Zdobyto brakującą treść pamiętnika:<br/>Szukajcie sumy liczb w miejscu gdzie zburzono mur.</p>*/}
                            </div>
                        )}

                        {item.ar_project_link && 
                            <>
                                <button
                                    className="quest-page__ar-tips-btn"
                                    onClick={() => setShowArTipsPopup(true)}
                                >
                                    Jak korzystać z AR ?
                                </button>
                                {isIOS && (
                                    <button
                                        className="quest-page__ios-camera-help-btn"
                                        onClick={() => setShowIosCameraHelpPopup(true)}
                                    >
                                        Widzisz czarny ekran?
                                    </button>
                                )}
                                <Button className='quest-page__open-ar-btn' variant="outlined" sx={btnCss()} onClick={() => handleShowARIframe()}>
                                    Otwórz AR
                                </Button>
                                
                                {showAR && <div className='quest-page__item-iframe'>
                                    <button
                                        className='quest-page__item-iframe-close-btn'
                                        onClick={() => setShowAR(false)}
                                        aria-label="Zamknij AR"
                                    >
                                        ✖
                                    </button>
                                    <iframe
                                        src={item.ar_project_link}
                                        width="100%" 
                                        height="100%"
                                        allow="camera;gyroscope;accelerometer;magnetometer;xr-spatial-tracking;microphone;"
                                        allowFullScreen>
                                    </iframe>
                                </div>}
                                {cameraError && (
                                    <p className="quest-page__camera-error-inline">
                                        ⚠ {cameraError}
                                    </p>
                                )}
                            </>
                        }            
                    
                    </div>   
                )          
            })}

            </div>
           
            {showScrollArrow && (
                <button
                    className="quest-page__scroll-arrow"
                    aria-label="Przewiń w dół"
                    onClick={() => contentRef.current?.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' })}
                >
                    ▼
                </button>
            )}

            {/* Lightbox z react-zoom-pan-pinch */}
            <ImageLightbox />

            {/* NOWY MODAL BŁĘDU KAMERY */}
            {isCameraErrorModalOpen && (
                <div className="quest-page__camera-error-overlay" onClick={() => setIsCameraErrorModalOpen(false)}>
                    <div className="quest-page__camera-error-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Nie można uruchomić kamery</h3>
                        <p>{cameraError}</p>
                        <Button
                            variant="outlined"
                            sx={btnCss()}
                            onClick={() => setIsCameraErrorModalOpen(false)}
                        >
                            Zamknij
                        </Button>
                    </div>
                </div>
            )}

            {wrongAnswerReveal && (
                <div className="quest-page__overlay">
                    <div className="quest-page__overlay-container">
                        <h3>Błędna odpowiedź</h3>
                        {wrongAnswerReveal.revealText
                            ? <p style={{ whiteSpace: 'pre-line' }}>{wrongAnswerReveal.revealText}</p>
                            : <p>Chodziło o odpowiedź: <strong>{wrongAnswerReveal.correctAnswer}</strong></p>}
                        <Button
                            variant="outlined"
                            sx={btnCss()}
                            onClick={() => setWrongAnswerReveal(null)}
                        >
                            Zamknij
                        </Button>
                    </div>
                </div>
            )}

            {questResolvedData && !questResolvedData.isSolver && (
                <div className="quest-page__overlay" onClick={() => setQuestResolvedData(null)}>
                    <div className="quest-page__overlay-container" onClick={(e) => e.stopPropagation()}>
                
                        <h3>Jeden z graczy rozwiązał zagadkę!</h3>
                        {questResolvedData.input_answer && <p>Poprawna odpowiedź: <strong>{questResolvedData.input_answer}</strong></p>}
                        <Button
                            variant="outlined"
                            sx={btnCss()}
                            onClick={() => setQuestResolvedData(null)}
                        >
                            Zamknij
                        </Button>
                     
                    </div>
                </div>
            )}

            {showArTipsPopup && createPortal(
                <div className="quest-page__ar-tips-overlay" onClick={() => setShowArTipsPopup(false)}>
                    <div className="quest-page__ar-tips-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="quest-page__ar-tips-close"
                            onClick={() => setShowArTipsPopup(false)}
                            aria-label="Zamknij porady"
                        >
                            ✖
                        </button>
                        <h3 className="quest-page__ar-tips-title">Porady do korzystania z AR</h3>
                        <ul className="quest-page__ar-tips-list">
                            {AR_TIPS.map((tip, i) => (
                                <li key={i} dangerouslySetInnerHTML={{ __html: tip }} />
                            ))}
                        </ul>
                    </div>
                </div>,
                document.body
            )}

            {showIosCameraHelpPopup && createPortal(
                <div className="quest-page__ar-tips-overlay" onClick={() => setShowIosCameraHelpPopup(false)}>
                    <div className="quest-page__ar-tips-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="quest-page__ar-tips-close"
                            onClick={() => setShowIosCameraHelpPopup(false)}
                            aria-label="Zamknij pomoc"
                        >
                            ✖
                        </button>
                        <h3 className="quest-page__ar-tips-title">Jak włączyć dostęp do kamery</h3>
                        <ul className="quest-page__ar-tips-list">
                            {IOS_CAMERA_HELP_STEPS.map((step, i) => (
                                <li key={i}>{step}</li>
                            ))}
                        </ul>
                    </div>
                </div>,
                document.body
            )}

        </div>
    );
};

export default QuestPage;